import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { CssBaseline, Paper, Typography, Grid, Card, CardContent, Button, Box, OutlinedInput, MenuItem, FormControl, Select } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

// Component imports
import Feedback from '../../components/Feedback/Feedback';

import ProviderService from '../../services/ProviderService/ProviderService';
import ProductService from '../../services/ProductService/ProductService';

import { REQUEST_TYPE_ADMIN } from '../../environment';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import CheckIcon from '@mui/icons-material/Check';

// Define component styling
const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
    },
    main: {
        flexGrow: 1,
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 'auto',
    },
    paper: {
        marginBottom: theme.spacing(3),
        marginLeft: theme.spacing(2),
        marginRight: theme.spacing(2),
        padding: theme.spacing(2),
        boxShadow: 'none',
        backgroundColor: '#fafafa',
        [theme.breakpoints.up(600 + theme.spacing(3) * 2)]: {
            marginBottom: theme.spacing(6),
            marginLeft: theme.spacing(6),
            marginRight: theme.spacing(6),
            padding: theme.spacing(3),
        },
    },
    typography: {
        fontWeight: 'bold',
        marginBottom: theme.spacing(2),
    },
    card: {
        border: '1px solid #a52a2a',
        backgroundColor: '#fff',
    },
    subCard: {
        backgroundColor: '#fff',
        boxShadow: 'none',
    },
    cardContent: {
        padding: '10px',
    },
    title: {
        fontSize: '14px',
        fontWeight: 'bold',
        backgroundColor: '#79232e',
        color: '#fff',
        padding: '8px',
        borderRadius: '4px',
        height: '30px',
        lineHeight: '30px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'left',
        marginBottom: '14px'
    },
    subTitle: {
        fontWeight: 'bold',
        marginBottom: '10px',
        color: '#79232e'
    },
    property: {
        fontSize: '12px',
        fontWeight: 600,
    },
    inputTextbox: {
        padding: '0 10px',
        height: '31px',
        borderRadius: '20px',
        border: '1px solid #d5c8c8',
        width: '100%',
        fontSize: '12px',
        '&:focus': {
            border: '2px solid #a52a2a',
            outline: 'none',
        },
    },
    inputTextboxArea: {
        padding: '0 10px',
        height: '130px',
        borderRadius: '20px',
        border: '1px solid #d5c8c8',
        width: '100%',
        fontSize: '12px',
        '&:focus': {
            border: '2px solid #a52a2a',
            outline: 'none',
        },
        '&::-webkit-scrollbar': {
            display: 'none',
        },
    },
    formControl: {
        width: '100%',
        '&:hover .MuiOutlinedInput-notchedOutline': {
            border: '1px solid #d5c8c8',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            border: '1px solid #d5c8c8',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: '1px solid #d5c8c8',
        },
    },
    select: {
        height: '31px',
        borderRadius: '14px',
        width: '100%',
        backgroundColor: '#fff',
        fontSize: '12px',
        borderColor: '#5',
        '&.MuiSelect-select': {
            backgroundColor: 'transparent',
        },
        '&:hover': {
        },
        '&.Mui-focused': {
            borderColor: '#d5c8c8 !important',
        },
        '& .MuiSelect-selectMenu': {
            backgroundColor: 'transparent',
        },
    },
    dropdownPlaceholder: {
        color: theme.palette.text.disabled,
        fontSize: '12px',
        fontStyle: 'normal',
        backgroundColor: '#fff',
    },
    outlinedInput: {
        fontSize: '12px',
        backgroundColor: '#fff',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#79232e !important',
            borderWidth: '0px !important',
            backgroundColor: '#fff',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#79232e !important',
            borderWidth: '0px !important',
            backgroundColor: '#fff',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#79232e !important',
            borderWidth: '0px !important',
            backgroundColor: '#fff',
        },
        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            backgroundColor: '#fff',
        },

    },
    menuItem: {
        fontSize: '12px',
        '&:hover': {
            color: '#fff',
            backgroundColor: '#a12e3d'
        },
        '&.Mui-selected': {
            color: '#fff',
            backgroundColor: '#79232e',
            '&:hover': {
                color: '#fff',
                backgroundColor: '#a12e3d'
            },
        },
    },
    value: {
        marginTop: 6,
        fontSize: 14,
        color: theme.palette.text.secondary,
    },
    switch: {
        '& .MuiSwitch-switchBase': {
            '&.Mui-checked': {
                color: '#fff',
                '& + .MuiSwitch-track': {
                    opacity: 1,
                    backgroundColor: '#79232e',
                },
            },
        }
    },
    saveButton: {
        marginTop: theme.spacing(3),
        marginLeft: theme.spacing(1),
        fontSize: '14px',
        textTransform: 'capitalize',
        borderRadius: '100px',
        paddingLeft: '2rem',
        paddingRight: '2rem',
        backgroundColor: '#79232e',
        fontWeight: 700,
        color: '#fff',
        '&:hover': {
            color: '#fff',
            backgroundColor: '#a12e3d'
        },
        '&:active': {
            color: '#fff',
            backgroundColor: '#79232e'
        },
        '&:focus': {
            color: '#fff',
            backgroundColor: '#79232e'
        }
    },
    closeButton: {
        marginTop: theme.spacing(3),
        marginLeft: theme.spacing(1),
        fontSize: '14px',
        textTransform: 'capitalize',
        borderRadius: '100px',
        paddingLeft: '2rem',
        paddingRight: '2rem',
        backgroundColor: '#fff',
        fontWeight: 700,
        color: '#79232e',
        border: '1px solid #a52a2a',
        '&:hover': {
            color: '#fff',
            backgroundColor: '#a12e3d',
            border: '1px solid #79232e'
        },
        '&:active': {
            color: '#fff',
            backgroundColor: '#79232e',
            border: '1px solid #79232e'
        },
        '&:focus': {
            color: '#fff',
            backgroundColor: '#79232e',
            border: '1px solid #79232e'
        }
    }
}));

const TransactionTemplate = (props) => {
    // Bind styling
    const classes = useStyles();

    const token = JSON.parse(localStorage.getItem('auth'));

    // Initialize connection to Host JavaScript API
    const host = props.host;

    const providerService = ProviderService();
    const productService = ProductService();

    const [loading, setLoading] = useState(true);
    const [templateName, setTemplateName] = useState(props.currentTransactionTemplate?.templateName || "");
    const [selectedProviders, setSelectedProviders] = useState(props.currentTransactionTemplate?.request.options.customProperties.provider || 'WaterFall');
    const [selectedProducts, setSelectedProducts] = useState(props.currentTransactionTemplate?.request.options.customProperties.productName || '');
    const [providers, setProviders] = useState([]);
    const [products, setProducts] = useState([]);
    const [openAlert, setopenAlert] = useState(false)
    const [errors, setErrors] = useState({
        templateName: '',
        provider: '',
        product: '',
    });
    const inputRef = useRef(null);

    const validateForm = () => {
        // const templateName = props.currentTransactionTemplate?.templateName || '';

        let valid = true;
        // Reset error messages
        setErrors({
            templateName: '',
            provider: '',
            product: '',
        });

        // Validate template name
        if (templateName.trim() === '') {
            setErrors(prev => ({ ...prev, templateName: 'Template name is required.' }));
            valid = false;
        }
        // Validate provider
        if (!selectedProviders) {
            setErrors(prev => ({ ...prev, provider: 'Provider is required.' }));
            valid = false;
        }

        // Validate product if provider is not WaterFall
        if (selectedProviders !== 'WaterFall' && !selectedProducts) {
            setErrors(prev => ({ ...prev, product: 'Product is required.' }));
            valid = false;
        }

        return valid;
    };

    const fetchProviders = async () => {
        setLoading(true);
        try {
            const data = await providerService.getProvider('FLOOD');
            setProviders(data || []);
        } catch (error) {
            console.error("Failed to fetch providers:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchProducts = async (provider) => {
        setLoading(true);
        try {
            const data = await productService.getProduct('FLOOD', provider)
            setProducts(data || []);
        } catch (error) {
            console.error("Failed to fetch products:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleChangeSelect = (event, type) => {
        const value = event.target.value;

        if (type === 'provider') {
            setSelectedProviders(value);

            // If the provider is 'WaterFall', reset selectedProducts to empty, since no product is required.
            if (value === 'WaterFall') {
                setSelectedProducts('');
            } else {
                fetchProducts(value);  // Fetch products if provider is not 'WaterFall'
            }
        } else if (type === 'product') {
            setSelectedProducts(value);
        }
    };

    const LoadingContainer = () => {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%', position: 'absolute', left: 0, bottom: 0, zIndex: 9999 }}>
                <Feedback message='Loading...' />
            </div>
        );
    };


    useEffect(() => {
        if (props.currentTransactionTemplate) {
            setTemplateName(props.currentTransactionTemplate.name);
            // setSelectedProviders(props.currentTransactionTemplate.request.options.customProperties.provider || 'WaterFall');
            // setSelectedProducts(props.currentTransactionTemplate.request.options.customProperties.productName || '');
        }
    }, [props.currentTransactionTemplate]);

    const handleClose = async () => {
        setopenAlert(false);
        const proxy = await host;

        // Invoke Host close method to navigate user away
        const close = await proxy.closeTemplate();
        return close;
    };


    const saveTransactionTemplate = async (e) => {
        // Await Host connection resolution
        const proxy = await host;
        e.preventDefault();
        const isValid = validateForm();
        if (isValid) {
            let transactionTemplate = {
                name: templateName,
                request: {
                    type: REQUEST_TYPE_ADMIN,
                    options: {
                        customProperties: {
                            provider: selectedProviders,
                            productName: selectedProducts,
                            isPrimaryBorrower: true,
                            isSecondaryBorrower: false,
                            isAddSecondaryBorrower: false
                        }
                    }
                }
            }

            try {

                const transactionTemplateSuccess = await proxy.saveTemplate(transactionTemplate);

                if (transactionTemplateSuccess === "success") {
                    setopenAlert(true);

                    //console.log('Successfully saved the transaction template!', transactionTemplateSuccess);
                } else {

                    //console.log('Save failed:', transactionTemplateSuccess.message || 'Unknown error');
                }

            } catch (error) {
                //console.error('Error saving the transaction template:', error);
            }
        }
    };

    // Interact with Host application to navigate user away
    const closeTransactionTemplate = async () => {
        // Await Host connection resolution
        const proxy = await host;

        // Invoke Host close method to navigate user away
        const close = await proxy.closeTemplate();
        return close;
    };


    useEffect(() => {
        // if (props.currentTransactionTemplate) {

        // }
        fetchProviders();
        // eslint-disable-next-line
    }, [props.currentTransactionTemplate]);


    // useEffect(() => {
    //     if (props.currentTransactionTemplate?.templateName) {
    //       setTemplateName(props.currentTransactionTemplate.templateName);
    //     }
    //   }, [props.currentTransactionTemplate]);
    const handleInputChange = (e) => {
        setTemplateName(e.target.value);  // Update the state with the new value
        if (e.target.value.trim() !== '') {
            setErrors((prev) => ({ ...prev, templateName: '' }));
        }

    };
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus(); // Ensure input field retains focus
        }
    }, [templateName]);
    // Initialized component bound to state data
    const InitializedTransactionTemplate = (props) => {
        return (
            <React.Fragment>
                <CssBaseline />
                <Paper className={classes.paper}>
                    <React.Fragment>


                        {/* ---------------Notification------------- */}
                        {openAlert && (
                            <Dialog
                                open={openAlert}
                                onClose={handleClose}
                                // PaperComponent={PaperComponent}
                                disableBackdropClick 
                                disableEscapeKeyDown 
                                aria-labelledby="draggable-dialog-title"
                            >
                                <DialogTitle style={{ cursor: 'move', display: 'flex', alignItems: 'center', gap: '8px' }} id="draggable-dialog-title">
                                    <CheckIcon sx={{ display: 'flex',justifyContent: 'center', alignItems: 'center', background: 'green', borderRadius: '50%', width: '20px', height: '20px', color: 'white', fontSize: '16px'
                                    }}/>
                                    Success
                                </DialogTitle>
                                <DialogContent>
                                    <DialogContentText >
                                        Template saved successfully.
                                    </DialogContentText>
                                </DialogContent>
                                <DialogActions>

                                    <Button onClick={handleClose}>Ok</Button>
                                </DialogActions>
                            </Dialog>)
                        }
                        <Typography component='h1' variant='h4' align='left' className={classes.typography}>
                            Transaction Template
                        </Typography>
                        <Card className={classes.card}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Card className={classes.subCard}>
                                        <CardContent className={classes.cardContent}>
                                            <Grid container spacing={2} style={{ position: 'relative' }}>
                                                {loading && (
                                                    <LoadingContainer />
                                                )}
                                                <Grid item xs={12}>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={6}>
                                                            <Typography variant="body2" className={classes.property}>Template Name</Typography>
                                                            <Typography className={classes.value}>
                                                                <input type="text" value={templateName  } ref={inputRef} onChange={handleInputChange} className={classes.inputTextbox} />
                                                            </Typography>
                                                            {errors.templateName && (
                                                                <Typography variant="body2" color="error" className={classes.errorMessage}>
                                                                    {errors.templateName}
                                                                </Typography>
                                                            )}
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={6}>
                                                            <Typography variant="body2" className={classes.property}>Provider</Typography>
                                                            <Typography className={classes.value}>
                                                                <FormControl className={classes.formControl}>
                                                                    <Select
                                                                        multiple={false}
                                                                        displayEmpty
                                                                        value={selectedProviders}
                                                                        onChange={(event) => handleChangeSelect(event, 'provider')}
                                                                        input={<OutlinedInput className={classes.outlinedInput} />}
                                                                        renderValue={(selected) => {
                                                                            if (!selected || selected.length === 0) {
                                                                                return <span className={classes.dropdownPlaceholder}><em style={{ fontStyle: 'normal' }}>Select provider...</em></span>;
                                                                            }
                                                                            return selected;
                                                                        }}
                                                                        className={classes.select}>
                                                                        {providers.map((provider) => (
                                                                            <MenuItem key={provider} value={provider} className={classes.menuItem}>
                                                                                {provider}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </FormControl>
                                                                {errors.provider && (
                                                                    <Typography variant="body2" color="error" className={classes.errorMessage}>
                                                                        {errors.provider}
                                                                    </Typography>
                                                                )}
                                                            </Typography>
                                                        </Grid>
                                                        {selectedProviders !== 'WaterFall' && (
                                                            <Grid item xs={6}>
                                                                <Typography variant="body2" className={classes.property}>Product</Typography>
                                                                <Typography className={classes.value}>
                                                                    <FormControl className={classes.formControl}>
                                                                        <Select
                                                                            multiple={false}
                                                                            displayEmpty
                                                                            value={selectedProducts}
                                                                            onChange={(event) => handleChangeSelect(event, 'product')}
                                                                            input={<OutlinedInput className={classes.outlinedInput} />}
                                                                            renderValue={(selected) => {
                                                                                if (!selected || selected.length === 0) {
                                                                                    return <span className={classes.dropdownPlaceholder}><em style={{ fontStyle: 'normal' }}>Select product...</em></span>;
                                                                                }
                                                                                return selected;
                                                                            }}
                                                                            className={classes.select}>
                                                                            {products.map((product) => (
                                                                                <MenuItem key={product} value={product} className={classes.menuItem}>
                                                                                    {product}
                                                                                </MenuItem>
                                                                            ))}
                                                                        </Select>
                                                                    </FormControl>
                                                                    {errors.product && (
                                                                        <Typography variant="body2" color="error" className={classes.errorMessage}>
                                                                            {errors.product}
                                                                        </Typography>
                                                                    )}
                                                                </Typography>
                                                            </Grid>
                                                        )}
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Card>
                        <Grid item xs={12}>
                            <Box display="flex" justifyContent="flex-end">
                                <Button variant="contained" color="primary" className={classes.saveButton} onClick={saveTransactionTemplate}>
                                    Save Transaction Template
                                </Button>
                                <Button variant="contained" color="primary" className={classes.closeButton} onClick={closeTransactionTemplate}>
                                    Close Transaction Template
                                </Button>
                            </Box>
                        </Grid>
                    </React.Fragment>
                </Paper>
            </React.Fragment>
        );
    };

    // Interface definition for initialized component
    InitializedTransactionTemplate.propTypes = {
        host: PropTypes.object.isRequired,
        currentTransactionTemplate: PropTypes.object
    };


    // Handler to render the relative component - initialized
    const RenderTransactionTemplate = (props) => {
        // If current transaction template data is present in state
        if (props.currentTransactionTemplate) {
            // Render initialized component
            return <InitializedTransactionTemplate host={props.host} currentTransactionTemplate={props.currentTransactionTemplate} />;
        } else {
            return <InitializedTransactionTemplate host={props.host} />;
        }
    };

    RenderTransactionTemplate.propTypes = {
        host: PropTypes.object.isRequired,
        currentTransactionTemplate: PropTypes.object
    };

    return (
        <RenderTransactionTemplate host={props.host} currentTransactionTemplate={props.currentTransactionTemplate} />
    );
};

TransactionTemplate.propTypes = {
    host: PropTypes.object.isRequired,
    currentTransactionTemplate: PropTypes.object
};

export default TransactionTemplate;